<template>
    <div class="mcq-sheet">
        <div v-if="loading" class="mcq-sheet-loading">
            <b-spinner small variant="primary" class="mr-2"></b-spinner> Loading answer sheet...
        </div>
        <div v-else-if="error" class="text-danger text-center py-3">{{ error }}</div>

        <div v-else>
            <div class="mcq-sheet-head">
                <div v-if="student">
                    <div class="mcq-sheet-student">{{ student.first_name || "" }} {{ student.last_name || "" }}</div>
                    <div class="mcq-sheet-muted">Roll No: {{ student.roll_number || "-" }}</div>
                </div>
                <div v-else class="mcq-sheet-student">{{ exam.name }}</div>
                <div class="mcq-sheet-chips">
                    <span class="mcq-sheet-chip">Score {{ formatMark(student ? student.pivot.score : score) }} / {{ formatMark(total) }}</span>
                    <span class="mcq-sheet-chip mcq-sheet-chip-success">{{ summary.correct }} Correct</span>
                    <span class="mcq-sheet-chip mcq-sheet-chip-danger">{{ summary.wrong }} Wrong</span>
                    <span class="mcq-sheet-chip">{{ summary.unanswered }} Not answered</span>
                </div>
            </div>

            <div v-for="(question, index) in questions" :key="question.id" class="mcq-sheet-question">
                <div class="mcq-sheet-question-text">
                    <span class="mr-1">{{ question.question_no || index + 1 }}.</span>
                    <span v-for="(qData, qdIndex) in questionParts(question)" :key="qdIndex" class="mr-1">
                        <!-- the editor saves question text as HTML -->
                        <span v-if="qData.type == 'text'" v-html="latexCodeToMathLive(qData.value)"></span>
                        <math-field v-if="qData.type == 'equation'" class="mx-1" style="border: none;" readonly>{{
                            qData.value
                        }}</math-field>
                        <a v-if="qData.type == 'image' && questionFile(qData.value, question)" target="_blank"
                            title="Click to view full image" :href="fileUrl(questionFile(qData.value, question))">
                            <img class="mcq-sheet-img" :src="fileUrl(questionFile(qData.value, question))">
                        </a>
                    </span>
                    <span :class="['mcq-sheet-result', 'mcq-sheet-result-' + questionResult(question)]">
                        {{ resultLabel(question) }}
                    </span>
                </div>

                <div v-for="choice in question.choices" :key="choice.id"
                    :class="['mcq-sheet-choice', choiceClass(question, choice)]">
                    <i v-if="choice.is_right_choice" class="fas fa-check-circle mr-1"></i>
                    <i v-else-if="isPicked(question, choice)" class="fas fa-times-circle mr-1"></i>
                    <i v-else class="far fa-circle mr-1"></i>
                    {{ choice.name }}
                    <span v-if="isPicked(question, choice)" class="mcq-sheet-picked">{{ student ? "Student's answer" : "Your answer" }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import "mathlive";
import { questionParts, questionResult, answerSummary, formatMark } from "./mcqSheet.js";
export default {
    // answer sheet of one submitted student for faculty, or without student the
    // logged in student's own sheet with their score
    props: ["exam", "student", "total", "score"],
    data() {
        return {
            loading: true,
            error: "",
            questions: [],
            answers: {}, // {question_id: answer}
            cloudStorageRoot: "",
        };
    },
    async created() {
        this.cloudStorageRoot = this.$store.getters.getAPIKey.userAttachments;
        await this.loadSheet();
    },
    computed: {
        summary() {
            return answerSummary(this.questions, this.answers);
        },
    },
    methods: {
        // $$latex$$ in the question text becomes a readonly math field
        latexCodeToMathLive(codeText) {
            if (codeText == null || codeText == "") return "";
            return codeText.replace(
                /\$\$(.*?)\$\$/g,
                '<math-field class="p-0" style="border: none;" readonly>$1</math-field>'
            );
        },
        async loadSheet() {
            this.loading = true;
            this.error = "";
            const examApi = this.$store.getters.getAPIKey.examination + `/${this.exam.id}`;
            try {
                const [questions, answers] = await Promise.all([
                    // answer_sheet: students get the correct choices once their score is viewable
                    this.$axios.get(`${examApi}/examquestions`, { params: this.student ? {} : { answer_sheet: 1 } }),
                    this.$axios.get(this.student
                        ? `${examApi}/institutionusers/${this.student.id}/examanswers`
                        : `${examApi}/institutionuser/examanswers`),
                ]);
                this.questions = questions.data || [];
                const answerMap = {};
                (answers.data || []).forEach((answer) => (answerMap[answer.examquestion_id] = answer));
                this.answers = answerMap;
            } catch (error) {
                this.error = (error.response && error.response.data && error.response.data.message) ||
                    "Failed to load answer sheet, please try again.";
            }
            this.loading = false;
        },
        questionParts,
        isPicked(question, choice) {
            const answer = this.answers[question.id];
            return !!answer && answer.choice_id == choice.id;
        },
        questionResult(question) {
            return questionResult(question, this.answers[question.id]);
        },
        resultLabel(question) {
            const result = this.questionResult(question);
            if (result == "unanswered") return "Not answered";
            const answer = this.answers[question.id];
            const label = result == "correct" ? "Correct" : "Wrong";
            return answer && answer.score != null ? `${label} (${this.formatMark(answer.score)})` : label;
        },
        choiceClass(question, choice) {
            if (Number(choice.is_right_choice)) return "mcq-sheet-choice-right";
            if (this.isPicked(question, choice)) return "mcq-sheet-choice-wrong";
            return "";
        },
        questionFile(fileId, question) {
            return (question.files || []).find((item) => item.id == fileId);
        },
        fileUrl(file) {
            return this.cloudStorageRoot + file.file_path + file.file_name;
        },
        formatMark,
    },
};
</script>

<style scoped>
.mcq-sheet {
    font-size: 13px;
    color: #2d3748;
}

.mcq-sheet-loading {
    text-align: center;
    padding: 24px 0;
    color: #718096;
}

.mcq-sheet-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding-bottom: 10px;
    margin-bottom: 10px;
    border-bottom: 1px solid #e2e8f0;
}

.mcq-sheet-student {
    font-size: 15px;
    font-weight: 700;
    color: #1a365d;
}

.mcq-sheet-muted {
    font-size: 12px;
    color: #718096;
}

.mcq-sheet-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.mcq-sheet-chip {
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    color: #4a5568;
    background: #edf2f7;
    border-radius: 999px;
}

.mcq-sheet-chip-success {
    color: #22753a;
    background: #e3f6e8;
}

.mcq-sheet-chip-danger {
    color: #b42318;
    background: #fdecea;
}

.mcq-sheet-question {
    padding: 10px 12px;
    margin-bottom: 8px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.mcq-sheet-question-text {
    margin-bottom: 6px;
    font-weight: 600;
}

.mcq-sheet-img {
    display: block;
    max-width: 220px;
    max-height: 140px;
    margin: 6px 0;
    object-fit: contain;
    border: 1px solid #dee2e6;
    border-radius: 6px;
}

.mcq-sheet-result {
    margin-left: 6px;
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 999px;
    white-space: nowrap;
}

.mcq-sheet-result-correct {
    color: #22753a;
    background: #e3f6e8;
}

.mcq-sheet-result-wrong {
    color: #b42318;
    background: #fdecea;
}

.mcq-sheet-result-unanswered {
    color: #4a5568;
    background: #edf2f7;
}

.mcq-sheet-choice {
    padding: 3px 8px;
    margin: 2px 0;
    border-radius: 6px;
    color: #4a5568;
}

.mcq-sheet-choice i {
    font-size: 12px !important;
}

.mcq-sheet-choice-right {
    color: #22753a;
    background: #e3f6e8;
    font-weight: 600;
}

.mcq-sheet-choice-wrong {
    color: #b42318;
    background: #fdecea;
    font-weight: 600;
}

.mcq-sheet-picked {
    margin-left: 6px;
    font-size: 11px;
    font-style: italic;
}
</style>
